import React from "react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { useDownloadSchedulerMutation } from "@/services/ofm-extract";
import { PropsSchedulerList, SizeDot } from "@/pages/tax/extracao-nf-ofm/dtos/ofm-dtos";
import { cn } from "@/lib/utils";
import { formatDateRange, formatDateTime2 } from "@/utils/format-date";
import { DownloadOfmStatus, DownloadStatusLabelPT, isReady, normalizeDownloadStatus } from "@/pages/tax/extracao-nf-ofm/utils/ofm.download.status";
import { colorByDownloadStatus } from "@/pages/tax/extracao-nf-ofm/utils/status.color";
import { downloadFromBlob } from "@/utils/download-blob";
import { OMF_TIME_ZONE, invoiceModelLabel } from "@/pages/tax/extracao-nf-ofm/constants/const";

const sizeCls: Record<SizeDot, string> = {
  sm: "w-2.5 h-2.5",
  md: "w-3 h-3",
  lg: "w-3.5 h-3.5",
};

type StatusDotProps = { status?: DownloadOfmStatus; size?: SizeDot; className?: string };

export const StatusDot: React.FC<StatusDotProps> = ({ status, size = "sm", className = "" }) => {
  const label = status ? DownloadStatusLabelPT[status] : "—";
  const color = status ? colorByDownloadStatus[status] : "bg-slate-400";

  return (
    <span className={cn("inline-flex items-center", className)} title={label} aria-label={label}>
      <span className={cn(sizeCls[size], "rounded-full", color)} />
      <span className="sr-only">{label}</span>
    </span>
  );
};


const Progress: React.FC<{ ready?: number; total?: number }> = ({ ready = 0, total = 0 }) => {
  const pct = total > 0 ? Math.round((ready / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="w-16 md:w-28 h-1.5 bg-slate-200 rounded overflow-hidden">
        <div className="h-1.5 bg-slate-600" style={{ width: `${pct}%` }} />
      </div>
      <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-700 shrink-0 whitespace-nowrap tabular-nums">
        {pct}%
      </span>
    </div>
  );
};

export default function OfmSchedulersList({
  items, isLoading, open, onToggle, renderExpanded,
}: PropsSchedulerList) {
  const [downloadScheduler, { isLoading: isDownloading }] = useDownloadSchedulerMutation();

  const handleDownload = async (schedulerId: string) => {
    try {
      const { blob, filename } = await downloadScheduler(schedulerId).unwrap();
      downloadFromBlob(blob, filename);
      
    } catch (e) {
      console.error("[ofm-download] failed", e);
    }
  };

  return (
    <Card className="mt-6">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Agendamentos</h2>
        </div>

        { }
        <div className="w-full overflow-x-auto">
          { }
          <div className="min-w-[980px]">
            { }
            <div className="hidden md:grid grid-cols-[24px_160px_1fr_100px_100px_90px_180px_160px] gap-3 px-3 py-2 bg-gray-50 text-xs text-gray-600 rounded-t">
              <div />
              <div>Criado em</div>
              <div>Período</div>
              <div>Modelo</div>
              <div>Tipo</div>
              <div>Total</div>
              <div>Progresso</div>
              <div className="text-right">Status / Ações</div>
            </div>

            {isLoading && items.length === 0 && (
              <div className="p-4 text-sm text-gray-500">Carregando…</div>
            )}

            {items.map((s) => {
              const period = formatDateRange(s.initial_date, s.final_date, { timeZone: 'UTC' });
              const createdAt = formatDateTime2(s.created_at, { timeZone: OMF_TIME_ZONE });

              const statusEn = normalizeDownloadStatus(s.download_status);
              const statusLabel = statusEn ? DownloadStatusLabelPT[statusEn] : "—";

              const toggle = () => onToggle(s.id);
              const total = s.files_total ?? s.total_jobs ?? 0;
              const ready = s.files_ready ?? 0;

              return (
                <div key={s.id} className="border-t first:border-0">
                  {/* desktop */}
                  <div className="hidden md:grid w-full grid-cols-[24px_160px_1fr_100px_100px_90px_180px_160px] gap-3 px-3 py-3 text-sm items-center">
                    <button type="button" onClick={toggle} className="text-gray-500 text-left">
                      {open[s.id] ? "▾" : "▸"}
                    </button>
                    <div className="truncate">{createdAt}</div>
                    <div className="truncate">{period}</div>
                    <div>{invoiceModelLabel(s.cod_invoice_model)}</div>
                    <div>{s.invoice_type === "S" ? "Saída" : "Entrada"}</div>
                    <div>{s.total_jobs}</div>
                    <div><Progress ready={ready} total={total} /></div>
                    <div className="flex items-center justify-end gap-3 whitespace-nowrap">
                      <div className="w-6 h-6 grid place-items-left shrink-0">
                        <StatusDot status={statusEn} />
                      </div>

                      <Button
                        size="sm"
                        variant="default"
                        className="min-w-[110px]"
                        disabled={!isReady(statusEn) || isDownloading}
                        onClick={() => handleDownload(s.id)}
                        title={statusLabel}
                        aria-label={`Baixar ZIP (${statusLabel})`}
                      >
                        Baixar ZIP
                      </Button>
                    </div>
                  </div>

                  {/* mobile */}
                  <div className="md:hidden px-3 py-3 text-sm space-y-2">
                    <div className="flex items-center justify-between">
                      <button type="button" onClick={toggle} className="text-gray-500">
                        {open[s.id] ? "▾" : "▸"}
                      </button>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 grid place-items-center shrink-0">
                          <StatusDot status={statusEn} />
                        </div>
                        <Button
                          size="sm"
                          className="min-w-[110px]"
                          disabled={!isReady(statusEn) || isDownloading}
                          onClick={() => handleDownload(s.id)}
                          title={statusLabel}
                          aria-label={`Baixar ZIP (${statusLabel})`}
                        >
                          Baixar ZIP
                        </Button>
                      </div>
                    </div>
                    <div className="text-xs text-slate-600">Criado em: {createdAt}</div>
                    <div className="text-xs text-slate-600">Período: {period}</div>
                    <div className="flex items-center gap-4">
                      <div>Modelo: {invoiceModelLabel(s.cod_invoice_model)}</div>
                      <div>Tipo: {s.invoice_type === "S" ? "Saída" : "Entrada"}</div>
                      <div>Total: {s.total_jobs}</div>
                    </div>
                    <Progress ready={ready} total={total} />
                  </div>

                  {open[s.id] && <div className="px-3 pb-4">{renderExpanded(s.id, s.cod_invoice_model)}</div>}
                </div>
              );
            })}

          </div>
        </div>
      </CardContent>
    </Card>
  );
}
